import { type RefObject, useEffect, useState } from 'react';

type ThresholdPosition = 'top' | 'bottom' | 'center';

const isAboveViewport = (element: HTMLElement, thresholdPosition: ThresholdPosition = 'bottom') => {
  const { bottom } = element.getBoundingClientRect();
  let threshold = 0;
  if (thresholdPosition === 'top') {
    threshold = element.clientHeight;
  } else if (thresholdPosition === 'center') {
    threshold = element.clientHeight / 2;
  }
  return bottom < threshold;
};

const isBelowViewport = (element: HTMLElement, thresholdPosition: ThresholdPosition = 'top') => {
  const { top } = element.getBoundingClientRect();
  let threshold = 0;
  if (thresholdPosition === 'bottom') {
    threshold = element.clientHeight;
  } else if (thresholdPosition === 'center') {
    threshold = element.clientHeight / 2;
  }
  return top > window.innerHeight - threshold;
};

type UseScrollTriggerOptions = {
  ref: RefObject<HTMLElement>;
  thresholdPosition?: ThresholdPosition;
};

export const useIsBelow = ({ ref, thresholdPosition }: UseScrollTriggerOptions) => {
  const [isBelow, setIsBelow] = useState(false);

  useEffect(() => {
    const checkPosition = () => {
      if (!ref.current) return;
      setIsBelow(isBelowViewport(ref.current, thresholdPosition));
    };

    window.addEventListener('scroll', checkPosition);
    checkPosition();

    return () => window.removeEventListener('scroll', checkPosition);
  }, [ref, thresholdPosition]);

  return isBelow;
};

const useIsAbove = ({ ref, thresholdPosition }: UseScrollTriggerOptions) => {
  const [isAbove, setIsAbove] = useState(false);

  useEffect(() => {
    const checkPosition = () => {
      if (!ref.current) return;

      setIsAbove(isAboveViewport(ref.current, thresholdPosition));
    };

    window.addEventListener('scroll', checkPosition);
    checkPosition();

    return () => window.removeEventListener('scroll', checkPosition);
  }, [ref, thresholdPosition]);

  return isAbove;
};

export const useViewportIsBetween = (refA: UseScrollTriggerOptions, refB: UseScrollTriggerOptions) => {
  const isAAbove = useIsAbove(refA);
  const isBBelow = useIsBelow(refB);

  return isAAbove && isBBelow;
};

export const useIsScrolled = ({ threshold = 0 }: { threshold?: number }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const checkPosition = (e: Event) => {
      if (isScrolled) return;

      const target = e.target as Document;
      if (target.scrollingElement?.scrollTop && target.scrollingElement.scrollTop > threshold) {
        setIsScrolled(true);
      }
    };

    window.addEventListener('scroll', checkPosition);

    return () => window.removeEventListener('scroll', checkPosition);
  }, []);

  return isScrolled;
};
